"use strict";

import { useState } from "react";
import { toast } from "react-toastify";

import { buildVoiceGenerationPayload } from "./voiceGenerationPayload.js";
import { ADD_REMIXED_VOICE_TO_CHARACTER } from "../../ApiEndpoints.js";
import { postWithFirebaseAuth } from "../../utils/firebaseApiClient.js";

export const VOICE_DESCRIPTION_MIN_LENGTH = 5;
export const VOICE_DESCRIPTION_MAX_LENGTH = 1000;
export const TEXT_MIN_LENGTH = 100;
export const TEXT_MAX_LENGTH = 1000;

export const EMOTION_OPTIONS = ["neutral", "happy", "sad", "angry", "afraid"];

const PROMPT_STRENGTH_PRESETS = {
  low: 0.25,
  medium: 0.5,
  high: 0.75,
};

const normalizeGenerationResponse = (responseData) => {
  if (Array.isArray(responseData)) return responseData;

  if (Array.isArray(responseData?.results)) {
    return responseData.results;
  }

  if (responseData?.previews) {
    return [responseData];
  }

  return [];
};

// The most specific reason the server gave, or a description of what went
// wrong when it gave none (no connection, expired session, no access).
export const voiceGenerationErrorMessage = (error) => {
  const response = error?.response;
  if (!response) {
    // A request that got no answer, or a problem in the page itself after
    // the server answered.
    return error?.isAxiosError || error?.request
      ? "Couldn't reach the server. Check your connection and try again."
      : `The takes were generated but couldn't be shown: ${
          error?.message || "unexpected error"
        }. Try again.`;
  }

  const data = response.data;
  const detail = data?.detail;
  const candidates = [
    detail?.error?.detail?.message,
    detail?.error?.message,
    detail?.message,
    detail?.error?.detail,
    detail?.error,
    detail,
  ];
  const specific = candidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );
  if (specific) return specific;

  if (response.status === 401) {
    return "Your session expired. Sign in again and retry.";
  }
  if (response.status === 403) {
    return "You don't have access to this project.";
  }
  if (response.status === 504 || response.status === 408) {
    return "The voice service took too long to respond. Try again, or with fewer characters.";
  }

  const general = [data?.message, data?.error].find(
    (candidate) => typeof candidate === "string" && candidate.trim()
  );
  return general ? `${general} (error ${response.status})` : null;
};

// Form state, generation and accepting previews for voice remix and voice
// design, shared by the full Character Customization page and the compact
// panel on the performance direction versions page.
const useVoiceCreation = ({
  title,
  endpoint,
  projectId,
  projectName,
  selectedCharacters,
  allowMultipleCharacters,
  defaultVoiceDescription = "Make this voice have a Mexican Accent",
  onVoiceAccepted,
}) => {
  const [selectedEmotion, setSelectedEmotion] = useState("neutral");

  const [voiceDescription, setVoiceDescription] = useState(
    defaultVoiceDescription
  );

  const [text, setText] = useState("");
  const [autoGenerateText, setAutoGenerateText] = useState(true);
  const [loudness, setLoudness] = useState(0.5);
  const [seed] = useState("");
  const [guidanceScale, setGuidanceScale] = useState(2);
  const [streamPreviews] = useState(false);
  const [remixingSessionId] = useState("");
  const [remixingSessionIterationId] = useState("");
  const [promptStrengthPreset, setPromptStrengthPreset] = useState("medium");

  const [voiceResults, setVoiceResults] = useState([]);
  const [acceptedVoiceIds, setAcceptedVoiceIds] = useState([]);
  const [acceptingVoiceIds, setAcceptingVoiceIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validatePayload = () => {
    const cleanVoiceDescription = voiceDescription.trim();

    if (selectedCharacters.length === 0) {
      toast.error(
        allowMultipleCharacters
          ? "Please select at least one character."
          : "Please select one character."
      );
      return false;
    }

    if (
      cleanVoiceDescription.length < VOICE_DESCRIPTION_MIN_LENGTH ||
      cleanVoiceDescription.length > VOICE_DESCRIPTION_MAX_LENGTH
    ) {
      toast.error("Voice description must be between 5 and 1000 characters.");
      return false;
    }

    const cleanText = text.trim();

    if (!autoGenerateText && !cleanText) {
      toast.error(
        "Enter preview text or enable automatic preview text generation."
      );
      return false;
    }

    if (
      !autoGenerateText &&
      (cleanText.length < TEXT_MIN_LENGTH || cleanText.length > TEXT_MAX_LENGTH)
    ) {
      toast.error("Preview text must be between 100 and 1000 characters.");
      return false;
    }

    if (seed !== "") {
      const seedNumber = Number(seed);

      if (
        !Number.isInteger(seedNumber) ||
        seedNumber < 0 ||
        seedNumber > 2147483647
      ) {
        toast.error("Seed must be an integer between 0 and 2147483647.");
        return false;
      }
    }

    return true;
  };

  const handleAcceptPreview = async ({
    generatedVoiceId,
    speakerId,
    emotion,
  }) => {
    if (!generatedVoiceId) {
      toast.error("Missing generated voice id.");
      return;
    }

    if (!emotion || !String(emotion).trim()) {
      toast.error("Choose a name to save the voice as.");
      return;
    }

    if (!speakerId) {
      toast.error("Missing speaker id.");
      return;
    }

    try {
      setAcceptingVoiceIds((previous) => {
        if (previous.includes(generatedVoiceId)) return previous;
        return [...previous, generatedVoiceId];
      });

      await postWithFirebaseAuth(ADD_REMIXED_VOICE_TO_CHARACTER, {
        projectId,
        speakerId,
        emotion,
        voiceId: generatedVoiceId,
        project_id: projectId,
        speaker_id: speakerId,
        voice_id: generatedVoiceId,
      });

      setAcceptedVoiceIds((previous) => {
        if (previous.includes(generatedVoiceId)) return previous;
        return [...previous, generatedVoiceId];
      });

      toast.success("Voice added successfully.");
      onVoiceAccepted?.({ generatedVoiceId, speakerId, emotion });
    } catch (error) {
      console.error("Failed to add voice:", error);
      toast.error("Failed to add voice.");
    } finally {
      setAcceptingVoiceIds((previous) =>
        previous.filter((id) => id !== generatedVoiceId)
      );
    }
  };

  const handleGenerateVoices = async () => {
    if (!validatePayload()) return;

    try {
      setIsSubmitting(true);
      setVoiceResults([]);
      setAcceptedVoiceIds([]);
      setAcceptingVoiceIds([]);

      const characterVoices = selectedCharacters.map((character) => ({
        speakerId: character.speaker || "",
        emotion: selectedEmotion,
      }));
      const payload = buildVoiceGenerationPayload({
        projectId,
        projectName,
        voiceDescription: voiceDescription.trim(),
        text: autoGenerateText ? null : text.trim() || null,
        autoGenerateText,
        loudness: Number(loudness),
        seed: seed === "" ? null : Number(seed),
        guidanceScale: Number(guidanceScale),
        streamPreviews,
        remixingSessionId: remixingSessionId.trim() || null,
        remixingSessionIterationId: remixingSessionIterationId.trim() || null,
        promptStrength: PROMPT_STRENGTH_PRESETS[promptStrengthPreset],
        characterVoices,
      });

      const response = await postWithFirebaseAuth(endpoint, payload);

      const normalizedResults = normalizeGenerationResponse(response.data).map(
        (result, index) => ({
          ...result,
          parent_speaker_id:
            result.parent_speaker_id ||
            selectedCharacters[index]?.speaker ||
            selectedCharacters[0]?.speaker ||
            "",
          selected_emotion: selectedEmotion,
        })
      );

      setVoiceResults(normalizedResults);

      if (streamPreviews) {
        toast.info(
          "stream_previews is enabled, so the backend may return generated IDs without audio_base_64."
        );
      }

      if (normalizedResults.length === 0) {
        toast.warning(`${title} completed, but no previews were returned.`);
        return;
      }

      toast.success(`${title} previews generated successfully.`);
    } catch (error) {
      console.error(`Failed to run ${title}:`, error);
      toast.error(
        voiceGenerationErrorMessage(error) ||
          `Failed to create ${title.toLowerCase()} previews (error ${
            error?.response?.status ?? "unknown"
          }).`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    selectedCharacters.length > 0 &&
    voiceDescription.trim().length >= VOICE_DESCRIPTION_MIN_LENGTH &&
    (autoGenerateText ||
      (text.trim().length >= TEXT_MIN_LENGTH &&
        text.trim().length <= TEXT_MAX_LENGTH)) &&
    !isSubmitting;

  return {
    selectedEmotion,
    setSelectedEmotion,
    voiceDescription,
    setVoiceDescription,
    text,
    setText,
    autoGenerateText,
    setAutoGenerateText,
    loudness,
    setLoudness,
    guidanceScale,
    setGuidanceScale,
    promptStrengthPreset,
    setPromptStrengthPreset,
    voiceResults,
    acceptedVoiceIds,
    acceptingVoiceIds,
    isSubmitting,
    canSubmit,
    handleGenerateVoices,
    handleAcceptPreview,
  };
};

export default useVoiceCreation;
